import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { PlusIcon } from '@heroicons/react/solid';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import ReactPaginate from 'react-paginate';
import '../../Pagination.css';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import {
  useGetPaginatedUserVehiclesQuery,
  useGetUserVehiclePageChangeMutation
} from '../../Features/userVehicles';
import { useTheme } from '../../ThemeContext';
import {
  TruckIcon,
  PencilIcon,
  PlusCircleIcon
} from '@heroicons/react/outline';

function ManageVehiclesView({}) {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { isOnline } = useIsOnline();

  const [pager, setPager] = useState("");
  const [progress, setProgress] = useState(0);
  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);

  const user = useSelector(selectCurrentUser);

  const {
    data: vehicles,
    isLoading,
    isSuccess,
    isError,
    error
  } = useGetPaginatedUserVehiclesQuery({
    user_id: user.id,
    paginate: numRowsPerPage,
    page: pager
  });

  useEffect(() => {
    setProgress(30);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && vehicles?.data) {
      setCurrentItems(vehicles.data.data || []);
      setPageCount(
        Math.ceil((vehicles.data.total || 0) / numRowsPerPage)
      );
    }

    if (isError) {
      setProgress(100);
    }

    if (isSuccess) {
      setProgress(100);
    }

    return () => {};
  }, [vehicles, isLoading, isSuccess, isError, numRowsPerPage]);

  const [pageChange] = useGetUserVehiclePageChangeMutation();

  const onChangePageRows = async (rows) => {
    const rowsPerPage = window.parseInt(rows);

    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    try {
      setProgress(80);

      const resp = await pageChange({
        user_id: user.id,
        paginate: rowsPerPage,
        page: 1
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);
        setPageCount(
          Math.ceil(resp.data.total / rowsPerPage)
        );
        setNumRowsPerPage(rowsPerPage);
        setPager(1);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        err.message || "Something went wrong",
        "error"
      );
    }
  };

  const handlePageChange = async (currentPage) => {
    const pageNum = window.parseInt(currentPage) + 1;

    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    try {
      setProgress(80);

      const resp = await pageChange({
        user_id: user.id,
        paginate: numRowsPerPage,
        page: pageNum
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(resp.data.total / numRowsPerPage)
        );

        setPager(pageNum);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        err.message || "Something went wrong",
        "error"
      );
    }
  };

  const editVehicle = (e, id) => {
    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    navigate("/client/edit-vehicle/" + id);
  };

  const truncateText = (str) => {
    if (!str) return "";

    return str.length > 25
      ? str.substring(0, 25) + "..."
      : str;
  };

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">

        {/* PAGE HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">

          <div className="flex items-center gap-3">

            <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
              <TruckIcon className="w-6 h-6 text-[#80b643]" />
            </div>

            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                Vehicles
              </h1>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your registered vehicles
              </p>
            </div>

          </div>

          {/* ADD VEHICLE */}
          <Link
            to="/client/create-vehicle"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                       bg-[#80b643] hover:bg-[#719f3a]
                       text-white text-sm font-medium
                       shadow-sm transition-all duration-200
                       w-full sm:w-auto"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Vehicle</span>
          </Link>

        </div>

        {/* MAIN CARD */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {/* CARD HEADER */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">

            <div className="flex items-center justify-between">

              <div>
                <h2 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">
                  Registered Vehicles
                </h2>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  View and manage your vehicles
                </p>
              </div>

              {currentItems.length > 0 && (
                <div className="hidden sm:flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                    <TruckIcon className="w-4 h-4 text-[#80b643]" />
                  </div>

                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {vehicles?.data?.total || currentItems.length} vehicles
                  </span>
                </div>
              )}

            </div>

          </div>

          {/* TABLE */}
          {currentItems.length > 0 ? (

            <div className="overflow-x-auto">

              <table className="w-full min-w-[500px]">

                <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">

                  <tr>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-left text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Vehicle
                    </th>

                    <th
                      scope="col"
                      className="px-4 sm:px-6 py-3 text-right text-[11px] font-semibold
                                 text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Action
                    </th>

                  </tr>

                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                  {currentItems.map((data) => (

                    <tr
                      onClick={(e) => editVehicle(e, data.id)}
                      key={data.id}
                      className="group hover:bg-gray-50 dark:hover:bg-gray-800/60
                                 cursor-pointer transition-colors duration-150"
                    >

                      {/* VEHICLE */}
                      <td className="px-4 sm:px-6 py-4">

                        <div className="flex items-center gap-3">

                          <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20
                                          flex items-center justify-center flex-shrink-0">

                            <TruckIcon className="w-5 h-5 text-[#80b643]" />

                          </div>

                          <div className="min-w-0">

                            <div className="text-sm font-medium text-gray-800 dark:text-white truncate">

                              {truncateText(data.name)}

                            </div>

                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">

                              Vehicle

                            </div>

                          </div>

                        </div>

                      </td>

                      {/* ACTION */}
                      <td className="px-4 sm:px-6 py-4 text-right">

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            editVehicle(e, data.id);
                          }}
                          className="inline-flex items-center justify-center gap-1.5
                                     px-3 py-1.5 rounded-lg
                                     border border-gray-200 dark:border-gray-700
                                     text-xs font-medium
                                     text-gray-600 dark:text-gray-300
                                     hover:text-[#80b643]
                                     hover:border-[#80b643]
                                     hover:bg-[#80b643]/5
                                     transition-all duration-200"
                        >

                          <PencilIcon className="w-3.5 h-3.5" />

                          <span className="hidden sm:inline">
                            Edit
                          </span>

                        </button>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          ) : (

            /* EMPTY STATE */
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">

              <div className="w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                <TruckIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />

              </div>

              <h3 className="text-sm sm:text-base font-semibold text-gray-700 dark:text-gray-200">
                No vehicles found
              </h3>

              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                You have not registered any vehicles yet. Add a vehicle to get started.
              </p>

              <Link
                to="/client/create-vehicle"
                className="inline-flex items-center justify-center gap-2
                           mt-5 px-4 py-2.5 rounded-xl
                           bg-[#80b643] hover:bg-[#719f3a]
                           text-white text-sm font-medium
                           shadow-sm transition-all duration-200"
              >

                <PlusCircleIcon className="w-5 h-5" />

                Add Vehicle

              </Link>

            </div>

          )}

          {/* PAGINATION */}
          {currentItems.length > 0 && (

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between
                            gap-4 px-4 sm:px-6 py-4
                            border-t border-gray-200 dark:border-gray-800">

              {/* PAGINATION */}
              <ReactPaginate
                breakLabel="..."
                nextLabel={
                  <ChevronRightIcon
                    className="h-8 w-8 text-gray-400 dark:text-white
                               bg-white dark:bg-gray-800
                               cursor-pointer
                               hover:bg-gray-50 dark:hover:bg-gray-700"
                  />
                }
                onPageChange={(e) => handlePageChange(e.selected)}
                pageRangeDisplayed={3}
                pageCount={pageCount}
                previousLabel={
                  <ChevronLeftIcon
                    className="h-8 w-8 text-gray-400 dark:text-white
                               bg-white dark:bg-gray-800
                               cursor-pointer
                               hover:bg-gray-50 dark:hover:bg-gray-700"
                  />
                }
                renderOnZeroPageCount={null}
                containerClassName="pagination"
                pageLinkClassName="page-num"
                previousLinkClassName="controlBtn"
                nextLinkClassName="controlBtn"
                activeLinkClassName={`${
                  darkMode ? "" : "active"
                } dark:text-white bg-white dark:bg-gray-700
                   hover:dark:text-white hover:dark:bg-gray-600`}
              />

              {/* ROWS PER PAGE */}
              <div className="flex items-center justify-between sm:justify-end gap-2">

                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Rows per page
                </span>

                <select
                  value={numRowsPerPage}
                  onChange={(e) => onChangePageRows(e.target.value)}
                  className="w-20 px-2 py-1.5
                             text-xs text-gray-600 dark:text-gray-300
                             bg-white dark:bg-gray-800
                             border border-gray-200 dark:border-gray-700
                             rounded-lg outline-none
                             focus:border-[#80b643]
                             focus:ring-1 focus:ring-[#80b643]
                             cursor-pointer"
                >

                  <option
                    value="10"
                    className="text-gray-800 dark:text-white"
                  >
                    10
                  </option>

                  <option
                    value="25"
                    className="text-gray-800 dark:text-white"
                  >
                    25
                  </option>

                  <option
                    value="50"
                    className="text-gray-800 dark:text-white"
                  >
                    50
                  </option>

                  <option
                    value="100"
                    className="text-gray-800 dark:text-white"
                  >
                    100
                  </option>

                </select>

              </div>

            </div>

          )}

        </div>

      </div>

    </div>
  );
}

export default ManageVehiclesView;